import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  HasReinforcementPadsOptions,
  WeldedToShellOptions,
} from 'types/assetModel/assetModelHelpers'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { getDropdownOptionsForEnum } from 'utils/enum'
import { forecastMath } from 'utils/fcMath'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

const { round } = forecastMath

export interface GeckoSaddleSupportJSON {
  type: GeckoFeatureModelType.SaddleSupport
  name: string
  feature_geometry: {
    x: number
    y: number
  }
  surface_specs: {
    width: number
    height: number
    welded_to_shell: string
    has_reinforcement_pads: string
  }
}

export const NewGeckoSaddleSupportJSON: GeckoSaddleSupportJSON = {
  type: GeckoFeatureModelType.SaddleSupport,
  name: 'New Saddle Support',
  feature_geometry: {
    x: 0,
    y: 0,
  },
  surface_specs: {
    width: 0,
    height: 0,
    welded_to_shell: WeldedToShellOptions.Yes,
    has_reinforcement_pads: HasReinforcementPadsOptions.No,
  },
}

export class GeckoSaddleSupport extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.SaddleSupport
  readonly width: GeckoMeasurement
  readonly height: GeckoMeasurement
  readonly x: GeckoMeasurement
  readonly y: GeckoMeasurement
  readonly weldedToShell: WeldedToShellOptions
  readonly hasReinforcementPads: HasReinforcementPadsOptions

  private constructor(
    id: string,
    name: string,
    width: GeckoMeasurement,
    height: GeckoMeasurement,
    x: GeckoMeasurement,
    y: GeckoMeasurement,
    weldedToShell: WeldedToShellOptions,
    hasReinforcementPads: HasReinforcementPadsOptions
  ) {
    super(id)
    this.name = name
    this.width = width
    this.height = height
    this.x = x
    this.y = y
    this.weldedToShell = weldedToShell
    this.hasReinforcementPads = hasReinforcementPads
  }

  public static CreateFromJSON(
    componentID: string,
    saddleSupportID: string,
    saddleSupportJSON: GeckoSaddleSupportJSON
  ): GeckoSaddleSupport {
    const surfaceSpecs = saddleSupportJSON.surface_specs as Record<
      string,
      number | string
    >
    const featureGeometry = saddleSupportJSON.feature_geometry as Record<
      string,
      number
    >

    const width = GeckoMeasurement.CreateFromValue(
      surfaceSpecs.width as number,
      GeckoMeasurementUnit.Meters
    )
    const height = GeckoMeasurement.CreateFromValue(
      surfaceSpecs.height as number,
      GeckoMeasurementUnit.Meters
    )
    const x = GeckoMeasurement.CreateFromValue(
      featureGeometry.x as number,
      GeckoMeasurementUnit.Meters
    )
    const y = GeckoMeasurement.CreateFromValue(
      featureGeometry.y as number,
      GeckoMeasurementUnit.Meters
    )

    const weldedToShell =
      (surfaceSpecs.welded_to_shell as WeldedToShellOptions) ??
      WeldedToShellOptions.Yes
    const hasReinforcementPads =
      (surfaceSpecs.has_reinforcement_pads as HasReinforcementPadsOptions) ??
      HasReinforcementPadsOptions.No

    const saddleSupportName = saddleSupportJSON.name

    return new GeckoSaddleSupport(
      saddleSupportID,
      saddleSupportName,
      width,
      height,
      x,
      y,
      weldedToShell,
      hasReinforcementPads
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoSaddleSupportJSON>
  ): Map<string, GeckoSaddleSupport> {
    return Object.keys(recordsJSON).reduce((resultObj, saddleSupportID) => {
      resultObj.set(
        saddleSupportID,
        GeckoSaddleSupport.CreateFromJSON(
          componentID,
          saddleSupportID,
          recordsJSON[saddleSupportID]
        )
      )
      return resultObj
    }, new Map<string, GeckoSaddleSupport>())
  }

  public asJSON(): GeckoSaddleSupportJSON {
    return generateSaddleSupport(
      this.name,
      this.width,
      this.height,
      this.x,
      this.y,
      this.weldedToShell,
      this.hasReinforcementPads
    )
  }
}

export function generateSaddleSupport(
  name: string,
  width: GeckoMeasurement,
  height: GeckoMeasurement,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  weldedToShell: WeldedToShellOptions = WeldedToShellOptions.Yes,
  hasReinforcementPads: HasReinforcementPadsOptions = HasReinforcementPadsOptions.No
): GeckoSaddleSupportJSON {
  const saddleSupportJSON: GeckoSaddleSupportJSON = {
    type: GeckoFeatureModelType.SaddleSupport,
    name: name,
    feature_geometry: {
      x: round(x.toNumber(GeckoMeasurementUnit.Meters), 5),
      y: round(y.toNumber(GeckoMeasurementUnit.Meters), 5),
    },
    surface_specs: {
      width: round(width.toNumber(GeckoMeasurementUnit.Meters), 5),
      height: round(height.toNumber(GeckoMeasurementUnit.Meters), 5),
      welded_to_shell: weldedToShell,
      has_reinforcement_pads: hasReinforcementPads,
    },
  }

  return saddleSupportJSON
}

export const SaddleSupportFormFields = (
  saddleSupportJSON: GeckoSaddleSupportJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Saddle Support Name',
      value: saddleSupportJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'width',
      label: 'Width',
      value: saddleSupportJSON.surface_specs.width,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: saddleSupportJSON.surface_specs.height,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: saddleSupportJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: saddleSupportJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'welded_to_shell',
      label: 'Welded to Shell',
      value: saddleSupportJSON.surface_specs.welded_to_shell,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: getDropdownOptionsForEnum(WeldedToShellOptions),
    },
    {
      dataKey: 'has_reinforcement_pads',
      label: 'Has Reinforcement Pads',
      value: saddleSupportJSON.surface_specs.has_reinforcement_pads,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: getDropdownOptionsForEnum(HasReinforcementPadsOptions),
    },
  ]
}

export const SaddleSupportJSONFromFormFields = (formFields: FormField[]) => {
  const weldedToShell = getFormFieldByDataKey(formFields, 'welded_to_shell')
  const hasReinforcementPads = getFormFieldByDataKey(
    formFields,
    'has_reinforcement_pads'
  )

  return generateSaddleSupport(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'width')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    weldedToShell?.value as WeldedToShellOptions,
    hasReinforcementPads?.value as HasReinforcementPadsOptions
  )
}

export const SaddleSupportFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.SaddleSupport,
  title: 'Saddle Supports',
  singularName: 'Saddle Support',
  fields: [
    'name',
    'width',
    'height',
    'x',
    'y',
    'welded_to_shell',
    'has_reinforcement_pads',
  ],
  sortBy: [{ fieldName: 'x', order: 'asc' }],
}
